import { useSession } from "@clerk/clerk-react";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "@heroui/react";
import { Scales, Plus } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { API_URL } from "constants/index";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { CustomerInvoice } from "shared/types/customer-invoice";
import {
  LegalProcedure,
  LegalProcedureType,
  PartyRole,
} from "shared/types/legal-procedure";
import { applyMask } from "shared/utils/masks";
import { LegalProcedureForm } from "./LegalProcedureForm";
import { LegalProcedureDetail } from "./LegalProcedureDetail";

interface LegalProcedureModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: CustomerInvoice | null;
  onProceduresChanged?: () => void;
}

export const LegalProcedureModal = ({
  isOpen,
  onClose,
  invoice,
  onProceduresChanged,
}: LegalProcedureModalProps) => {
  const { session } = useSession();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [procedures, setProcedures] = useState<LegalProcedure[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [activeContractId, setActiveContractId] = useState<number | null>(null);

  const partnerId = invoice?.partner_id?.[0];
  const unitId = invoice?.unit?.id;

  // Fetch existing procedures and active contract when modal opens
  useEffect(() => {
    if (isOpen && partnerId) {
      fetchProcedures(partnerId);
      if (unitId) {
        fetchActiveContract(unitId);
      }
    } else if (!isOpen) {
      setProcedures([]);
      setShowAddForm(false);
      setActiveContractId(null);
    }
  }, [isOpen, partnerId, unitId]);

  const fetchActiveContract = async (unitId: string) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/contracts/unit/${unitId}/active`,
        { method: "GET" },
        session
      );

      if (response.ok) {
        const contracts = await response.json();
        if (contracts.length > 0) {
          setActiveContractId(contracts[0].id);
        }
      }
    } catch (error) {
      console.error("Error fetching active contract:", error);
    }
  };

  const fetchProcedures = async (partnerId: number) => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental/legal-procedures/partner/${partnerId}`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      setProcedures(data);
    } catch (error) {
      console.error("Error fetching legal procedures:", error);
      toast.error("Fout bij het ophalen van juridische procedures");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProcedure = useCallback(
    async (data: {
      type: LegalProcedureType;
      partyRole: PartyRole;
      caseNumber?: string;
      court?: string;
      claimedAmount?: number;
      remarks?: string;
    }) => {
      if (!partnerId) return;

      if (!activeContractId) {
        toast.error("Geen actief huurcontract gevonden voor deze unit");
        return;
      }

      setIsSaving(true);
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/legal-procedures`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...data,
              rentalContractId: activeContractId,
              odooPartnerId: partnerId,
              contactId: invoice?.contact?.id,
              companyId: invoice?.company?.id,
              issues: invoice?.amount_residual
                ? [`Huurachterstand: ${applyMask(invoice.amount_residual, "valuta")} (stand ${new Date().toLocaleDateString("nl-BE")})`]
                : [],
            }),
          },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to create procedure: ${response.status}`);
        }

        const newProcedure = await response.json();
        setProcedures((prev) => [
          { ...newProcedure, procedureDates: [] },
          ...prev,
        ]);
        setShowAddForm(false);
        toast.success("Juridische procedure aangemaakt");
        onProceduresChanged?.();
      } catch (error) {
        console.error("Error creating legal procedure:", error);
        toast.error("Fout bij het aanmaken van juridische procedure");
      } finally {
        setIsSaving(false);
      }
    },
    [partnerId, activeContractId, invoice, session, onProceduresChanged]
  );

  const handleProcedureUpdated = useCallback(
    (updated: LegalProcedure) => {
      setProcedures((prev) =>
        prev.map((p) => (p.id === updated.id ? updated : p))
      );
      onProceduresChanged?.();
    },
    [onProceduresChanged]
  );

  const handleProcedureDeleted = useCallback((procedureId: number) => {
    setProcedures((prev) => prev.filter((p) => p.id !== procedureId));
    onProceduresChanged?.();
  }, [onProceduresChanged]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalContent>
        <ModalHeader className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <Scales
              weight="duotone"
              size={24}
              className="text-moss-primary"
            />
            <span>
              Juridische procedures - {invoice?.invoice_partner_display_name}
            </span>
          </div>
        </ModalHeader>

        <ModalBody>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner label="Procedures ophalen..." color="success" />
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {/* Add new procedure form */}
              {showAddForm ? (
                <LegalProcedureForm
                  onSubmit={handleCreateProcedure}
                  onCancel={() => setShowAddForm(false)}
                  isSaving={isSaving}
                />
              ) : (
                <ButtonNew
                  variant="secondaryMoss"
                  icon={<Plus size={14} />}
                  label="Nieuwe procedure"
                  onClick={() => setShowAddForm(true)}
                />
              )}

              {/* Procedures list */}
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-neutral-secondary-mid">
                  Procedures ({procedures.length})
                </h4>

                {procedures.length === 0 ? (
                  <p className="text-sm text-neutral-secondary-mid italic py-4 text-center">
                    Nog geen juridische procedures
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {procedures.map((procedure) => (
                      <LegalProcedureDetail
                        key={procedure.id}
                        procedure={procedure}
                        onProcedureUpdated={handleProcedureUpdated}
                        onProcedureDeleted={handleProcedureDeleted}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <ButtonNew
            variant="secondaryNeutral"
            label="Sluiten"
            onClick={onClose}
          />
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LegalProcedureModal;
